import React, { useState } from "react";
import { SelectChangeEvent } from "@mui/material";
import api from "../../../api/api";
import { useQuery, useQueryClient } from "react-query";
import {
  getLeadTimes,
  getPaintLeadTimes,
  updatePaintLeadTimes,
} from "../../../api/DzoneApi";
import dealerRoles from "../../../util/dealerAccounts";

interface LeadTimeState {
  bifold?: string;
  slider?: string;
  swing?: string;
  pivot?: string;
  bifoldNorth?: string;
  sliderNorth?: string;
  swingNorth?: string;
  pivotNorth?: string;
}

export interface LeadTimeObj {
  _id?: string;
  doorType: string;
  leadTime: string;
  leadTimeNorthMonth?: string;
  position: number;
}

export interface PaintLeadTimesObj {
  socalPaint?: string;
  socalPrime?: string;
  socalClad?: string;
  norcalPaint?: string;
  norcalPrime?: string;
  norcalClad?: string;
}

function compare(a: LeadTimeObj, b: LeadTimeObj) {
  if (a.position < b.position) {
    return -1;
  }
  if (a.position > b.position) {
    return 1;
  }
  return 0;
}

function useLeadTimesSelector() {
  const role = JSON.parse(sessionStorage.getItem("role") || JSON.stringify(""));

  const hasAdminAccess = role === "Admin" || role === "Inside Sales";

  const hasDealerAccess = [...dealerRoles, "Outside Sales"].includes(role);

  const staleTime = hasDealerAccess ? 300000 : 0;

  const { data, isFetching, isLoading } = useQuery(
    "LeadTimes",
    async () => {
      const response = await getLeadTimes();

      if (!hasDealerAccess) {
        response.data.lead.forEach((lead: LeadTimeObj) => {
          lead.doorType === "Bi-Fold" &&
            setState((prevState) => ({
              ...prevState,
              bifoldNorth: lead.leadTimeNorthMonth,
            }));
          lead.doorType === "Slider" &&
            setState((prevState) => ({
              ...prevState,
              sliderNorth: lead.leadTimeNorthMonth,
            }));
          lead.doorType === "Swing" &&
            setState((prevState) => ({
              ...prevState,
              swingNorth: lead.leadTimeNorthMonth,
            }));
          lead.doorType === "Pivot" &&
            setState((prevState) => ({
              ...prevState,
              pivotNorth: lead.leadTimeNorthMonth,
            }));
        });
      }

      return response.data;
    },
    { staleTime, keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const { data: paintData, isFetching: isFetchingPaint } = useQuery(
    "PaintLeadTimes",
    async () => {
      const response = await getPaintLeadTimes();

      if (!hasDealerAccess) {
        let lt: PaintLeadTimesObj = {};

        for (const key in response.data) {
          lt[key as keyof PaintLeadTimesObj] = String(response.data[key]);
        }

        setPaintLead(lt);
      }

      return response.data;
    },
    { staleTime, refetchOnWindowFocus: false }
  );

  const queryClient = useQueryClient();

  const [edit, setEdit] = useState(false);

  const [state, setState] = useState<LeadTimeState>({
    bifold: "",
    slider: "",
    swing: "",
    pivot: "",
    bifoldNorth: "",
    sliderNorth: "",
    swingNorth: "",
    pivotNorth: "",
  });

  const [paintLead, setPaintLead] = useState<PaintLeadTimesObj>({
    socalPaint: "",
    socalPrime: "",
    socalClad: "",
    norcalPaint: "",
    norcalPrime: "",
    norcalClad: "",
  });

  const handleChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setState({ ...state, [name]: value });
  };

  const handleChangePaint = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setPaintLead({ ...paintLead, [name]: value });
  };

  const [openPaintModal, setOpenPaintModal] = useState(false);

  const handleOpenPaintModal = () => {
    let lt: PaintLeadTimesObj = {};

    for (const key in paintData) {
      lt[key as keyof PaintLeadTimesObj] = String(paintData[key]);
    }

    setPaintLead(lt);
    setOpenPaintModal(true);
  };

  const handleClosePaintModal = () => {
    setPaintLead({
      socalPaint: "",
      socalPrime: "",
      socalClad: "",
      norcalPaint: "",
      norcalPrime: "",
      norcalClad: "",
    });
    setOpenPaintModal(false);
  };

  const changeToEditMode = () => setEdit((prevEdit) => !prevEdit);

  const handleAdminLeadTimeUpdate = async () => {
    try {
      await api.post("/api/admin/changeLeadTimes", { state });
      setEdit(false);
      return queryClient.invalidateQueries("LeadTimes");
    } catch (err) {
      console.log(err);
    }
  };

  const handleAdminPaintLeadTimeUpdate = async () => {
    const res = await updatePaintLeadTimes(paintLead);

    if (res.data.matchedCount) {
      handleClosePaintModal();
      return queryClient.invalidateQueries("PaintLeadTimes");
    }
  };

  const publicIds = {
    bifoldPID: "v1635811395/LeadTimes/bifoldDzone",
    sliderPID: "v1635811397/LeadTimes/sliderDzone",
    swingPID: "v1635811399/LeadTimes/swingDzone",
    pivotPID: "/LeadTimes/pivotDzone_v3",
  };

  const leadTimes = isFetching ? [] : data.lead.sort(compare);

  const region = isFetching ? "" : data.region;

  return {
    publicIds,
    handleAdminLeadTimeUpdate,
    handleAdminPaintLeadTimeUpdate,
    changeToEditMode,
    handleClosePaintModal,
    handleOpenPaintModal,
    openPaintModal,
    handleChangePaint,
    handleChange,
    paintLead,
    state,
    edit,
    leadTimes,
    region,
    isFetching,
    hasDealerAccess,
    hasAdminAccess,
    isLoading
  };
}

export default useLeadTimesSelector;
